/**
 * Responsibility: Implement object emoji behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { router } from '../ai/config'

const DEFAULT_EMOJI = '📦'
const CACHE_KEY = 'am2_object_emoji_cache_v2'
const FAILED_RETRY_MS = 60_000

const VISUAL_EFFECTS = new Set(['none', 'glow', 'pulse', 'float', 'shimmer', 'tilt'] as const)

export type ObjectVisualEffect = 'none' | 'glow' | 'pulse' | 'float' | 'shimmer' | 'tilt'

export type ObjectVisual = {
  emoji: string
  effect: ObjectVisualEffect
  decoPath: string | null
}

const visualCache = new Map<string, ObjectVisual>()
const inflight = new Set<string>()
const failedUntil = new Map<string, number>()
const listeners = new Set<() => void>()

let cacheLoaded = false

function normalizeName(name: string): string {
  return String(name ?? '').trim().toLowerCase()
}

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

function loadCacheOnce(): void {
  if (cacheLoaded || !canUseStorage()) return
  cacheLoaded = true
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    for (const [k, v] of Object.entries(parsed)) {
      if (!k || !v) continue
      if (typeof v === 'string') {
        const emoji = sanitizeEmoji(v)
        if (!emoji) continue
        visualCache.set(k, { emoji, effect: 'none', decoPath: null })
        continue
      }
      if (typeof v !== 'object') continue
      const emoji = sanitizeEmoji((v as { emoji?: string }).emoji ?? '')
      if (!emoji) continue
      visualCache.set(k, {
        emoji,
        effect: sanitizeEffect((v as { effect?: string }).effect),
        decoPath: sanitizeDecoPath((v as { decoPath?: string }).decoPath),
      })
    }
  } catch {
    // Ignore malformed cache payloads
  }
}

function persistCache(): void {
  if (!canUseStorage()) return
  try {
    const payload = Object.fromEntries(visualCache.entries())
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore quota/storage failures
  }
}

function sanitizeEmoji(input: string): string | null {
  const text = String(input ?? '').trim()
  if (!text) return null
  const m = text.match(/\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*/u)
  return m?.[0] ?? null
}

function sanitizeEffect(effect: string | undefined): ObjectVisualEffect {
  const value = String(effect ?? '').trim().toLowerCase()
  return VISUAL_EFFECTS.has(value as ObjectVisualEffect) ? (value as ObjectVisualEffect) : 'none'
}

function sanitizeDecoPath(input: string | undefined): string | null {
  const text = String(input ?? '').trim()
  if (!text) return null
  if (text.length > 240) return null
  if (!/^[MmLlHhVvCcSsQqTtAaZz0-9.,\-\s]+$/.test(text)) return null
  return text
}

function neutralVisual(): ObjectVisual {
  return { emoji: DEFAULT_EMOJI, effect: 'none', decoPath: null }
}

function emitChanged(): void {
  for (const fn of listeners) {
    try {
      fn()
    } catch {
      // Listener errors should not break emitter
    }
  }
}

async function fetchVisualFromAI(objectName: string): Promise<ObjectVisual | null> {
  const result = await router.route<{ emoji?: string; effect?: string; decoPath?: string }>({
    jobType: 'logic_forge',
    systemPrompt: [
      'You assign visual style to fantasy game objects.',
      'Rules:',
      '1) Return strict JSON only.',
      '2) Use this exact schema: {"emoji":"<one_emoji>","effect":"<none|glow|pulse|float|shimmer|tilt>","decoPath":"<svg_path_d_or_empty>"}',
      '3) Exactly one emoji character cluster, no extra text.',
      '4) decoPath must be a simple SVG path d string for a 24x24 icon, or empty string.',
      '5) Choose emoji and decoPath semantically from the object name.',
    ].join('\n'),
    userPrompt: `Object name: ${objectName}`,
  })
  const emoji = sanitizeEmoji(result?.emoji ?? '')
  if (!emoji) return null
  return {
    emoji,
    effect: sanitizeEffect(result?.effect),
    decoPath: sanitizeDecoPath(result?.decoPath),
  }
}

async function resolveObjectVisual(rawName: string, key: string): Promise<void> {
  try {
    const next = await fetchVisualFromAI(rawName)
    if (!next) {
      failedUntil.set(key, Date.now() + FAILED_RETRY_MS)
      return
    }
    visualCache.set(key, next)
    failedUntil.delete(key)
    persistCache()
    emitChanged()
  } catch {
    failedUntil.set(key, Date.now() + FAILED_RETRY_MS)
  } finally {
    inflight.delete(key)
  }
}

function queueResolve(rawName: string, key: string): void {
  if (!key || inflight.has(key)) return
  const retryAt = failedUntil.get(key) ?? 0
  if (retryAt > Date.now()) return
  inflight.add(key)
  void resolveObjectVisual(rawName, key)
}

export function onObjectEmojiChanged(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getObjectVisual(name: string): ObjectVisual {
  loadCacheOnce()
  const raw = String(name ?? '').trim()
  if (!raw) return neutralVisual()
  const key = normalizeName(raw)
  const cached = visualCache.get(key)
  if (cached) return cached
  queueResolve(raw, key)
  return neutralVisual()
}

export function getObjectEmoji(name: string): string {
  return getObjectVisual(name).emoji
}

export function decorateObjectName(name: string): string {
  const text = String(name ?? '').trim()
  if (!text) return ''
  return `${getObjectEmoji(text)} ${text}`
}
